 import React, { createContext, useState, useContext } from 'react';
import { registerUser, sendAnswers } from '../services/api'; // Importamos las funciones del servicio API
import { CATEGORY_IMAGE_MAP } from "../utils/categoryImages"; // Importamos el mapa de imágenes de categorías
const QuizContext = createContext();

export const useQuizContext = () => useContext(QuizContext);

export const QuizProvider = ({ children }) => {
  // Estado para los datos del usuario
  const [userData, setUserData] = useState({
    name: '',
    email: '',
    phone: '',
    dataConsent: false,
    ageRange: ''
  });

  // Estado para las preguntas del quiz
  const [questions, setQuestions] = useState([]);

  // Estado para las respuestas del usuario
  const [answers, setAnswers] = useState([]);

  // Estado para el resultado final
  const [quizResult, setQuizResult] = useState(null);

  // Estado para controlar en qué paso del proceso estamos
  const [currentStep, setCurrentStep] = useState(0); // 0: Welcome, 1: Registration, 2: Quiz, 3: Results

  // Estado para controlar qué pregunta estamos mostrando
  const [currentQuestion, setCurrentQuestion] = useState(0);

  // Estado para controlar si estamos enviando datos al servidor
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para manejar la carga de preguntas
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

  // Estado para almacenar errores del servidor
  const [serverError, setServerError] = useState(null);

  // Función para actualizar los datos del usuario
  const updateUserData = (data) => {
    setUserData({ ...userData, ...data });
  };

  // Función para guardar una respuesta
  const saveAnswer = (questionId, answerId, questionTitle, answerText) => {
    // Creamos una copia del array de respuestas
    const newAnswers = [...answers];
    
    // Verificamos si ya existe una respuesta para esta pregunta
    const existingAnswerIndex = newAnswers.findIndex(a => a.questionId === questionId);
    
    // Si existe, la actualizamos; si no, la añadimos
    if (existingAnswerIndex !== -1) {
      newAnswers[existingAnswerIndex] = { 
        questionId, 
        answerId, 
        questionTitle, 
        answerText 
      };
    } else {
      newAnswers.push({ 
        questionId, 
        answerId, 
        questionTitle, 
        answerText 
      });
    }
    
    setAnswers(newAnswers);
  };

  // Función para navegar a la siguiente pregunta o a los resultados si es la última
  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Si es la última pregunta, enviamos las respuestas y vamos a la pantalla de resultados
      submitAnswers();
    }
  };

  // Función para volver a la pregunta anterior
  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  // Función para enviar las respuestas al backend
  const submitAnswers = async () => {
    // Cambiamos el estado para mostrar que estamos procesando
    setIsSubmitting(true);
    
    try {
      console.log('Enviando respuestas:', answers);
      
      // Llamar al servicio API para enviar las respuestas
      const result = await sendAnswers(userData, answers);
      console.log('Resultado obtenido:', result);
      
      // Guardamos el resultado en el estado
      setQuizResult(result);
      
      // Avanzamos a la pantalla de resultados
      setCurrentStep(3);
    } catch (error) {
      console.error('Error al enviar las respuestas:', error);
      
      // En caso de error, usamos un resultado de prueba
      const mockResult = {
        "Tipo de licor": "Ron gran reserva",
        "Descripción": "Vos sos un parchado que disfruta de historia, calidad y exclusividad, ¡como un buen Ron Gran Reserva! Tiene ese toque clásico, con carácter y tradición, pero también es para los que buscan algo que te reconforte y te lleve en un viaje por lo auténtico. Es un 80% de vos, parce.",
        "código": "gran_reserva",
        "Coctel sugerido": "Para preparar un cóctel fácil que te encante, mezcla 2 onzas de Ron Gran Reserva con hielo, añade una rodaja de limón y unas gotas de cola o gaseosa. Sirve en un vaso largo, con hielo y una rodaja de limón para decorar. Listo, a disfrutar con parcero, ¡salud!"
      };
      
      setQuizResult(mockResult);
      setCurrentStep(3);
    } finally {
      // Independientemente del resultado, cambiamos el estado al finalizar
      setIsSubmitting(false);
    }
  };

  // Función para iniciar el quiz después del registro
  const startQuiz = async () => {
    setIsSubmitting(true);
    setIsLoadingQuestions(true);
    setServerError(null); // Reiniciar el error del servidor

    try {
      // Llamar al servicio API para obtener las preguntas
      const response = await registerUser(userData);
      console.log('Respuesta obtenida:', response);
      console.log('Tipo de respuesta:', typeof response);
      console.log('Es array:', Array.isArray(response));
      
      // Verificar si hay un mensaje de error específico de usuario ya registrado
      if (response && response.statusCode === 404) {
        // Establecer el error del servidor
        setServerError({
          type: 'alreadyRegistered',
          message: response.error || 'Ya has realizado esta experiencia anteriormente.'
        });
        
        // IMPORTANTE: Detenemos la ejecución aquí y no avanzamos a la siguiente pantalla
        console.log('Usuario ya registrado, mostrando modal');
        setIsSubmitting(false);
        setIsLoadingQuestions(false);
        return; // Retornamos para evitar la ejecución del resto de la función
      }
      
      // Verificar si la respuesta tiene un formato de error no manejado
      if (response && typeof response === 'object' && response.statusCode && response.statusCode !== 200) {
        console.warn('⚠️ Respuesta con código de error no manejado:', response);
        throw new Error(`Error del servidor: ${response.error || 'Error desconocido'}`);
      }
      
      // Si llegamos aquí, tenemos preguntas válidas (o el array vacío se manejó con los datos de prueba)
      const fetchedQuestions = response;
      console.log('Preguntas obtenidas:', fetchedQuestions);
      
      // Validar que tenemos preguntas y que es un array válido
      if (!fetchedQuestions || !Array.isArray(fetchedQuestions) || fetchedQuestions.length === 0) {
        console.warn('⚠️ No se recibieron preguntas válidas:', fetchedQuestions);
        throw new Error('No se recibieron preguntas válidas');
      }
      
      // Validar que cada pregunta tiene la estructura esperada
      // El webhook devuelve "titulo" y "respuestas" (no "titulo_pregunta" y "respuestas_preguntas")
      const invalidQuestions = fetchedQuestions.filter(q => 
        !q.id || (!q.titulo_pregunta && !q.titulo) || (!Array.isArray(q.respuestas_preguntas) && !Array.isArray(q.respuestas))
      );
      
      if (invalidQuestions.length > 0) {
        console.warn('⚠️ Algunas preguntas no tienen la estructura esperada:', invalidQuestions);
        throw new Error('Las preguntas recibidas no tienen el formato correcto');
      }
      
      // Transformamos las preguntas al formato que necesitamos
      const formattedQuestions = fetchedQuestions.map(question => {
        // Determinamos qué estructura usar (webhook vs mock data)
        const questionTitle = question.titulo_pregunta || question.titulo;
        const questionAnswers = question.respuestas_preguntas || question.respuestas;
        
        // Validación adicional por pregunta individual
        if (!questionAnswers || !Array.isArray(questionAnswers)) {
          console.warn('⚠️ Pregunta sin respuestas válidas:', question);
          throw new Error(`La pregunta "${questionTitle}" no tiene respuestas válidas`);
        }
        
        return {
          id: question.id,
          question: questionTitle,
          options: questionAnswers.map((respuesta, index) => {
            return {
              id: index + 1,
              text: respuesta.respuesta || '',
              category: respuesta.categoria || '',
              // Asignamos imágenes temporales basadas en categorías
              imageUrl: CATEGORY_IMAGE_MAP[respuesta.categoria] || '/assets/categorias/defaultImage.png', 
            };
          })
        };
      });
      
      setQuestions(formattedQuestions);
      
      // Reiniciamos las respuestas
      setAnswers([]);
      
      // Reiniciamos la pregunta actual
      setCurrentQuestion(0);
      
      // Avanzamos a la pantalla del quiz
      setCurrentStep(2);
    } catch (error) {
      console.error('Error al iniciar el quiz:', error);
      
      // Si hay un error específico de usuario ya registrado, no mostramos preguntas
      if (serverError && serverError.type === 'alreadyRegistered') {
        console.log('Error de usuario ya registrado, no avanzando al quiz');
        // No hacemos nada más, simplemente permanecemos en la pantalla de registro
      } else {
        // Para otros tipos de errores, podemos usar datos de prueba (opcional)
        console.log('🔸 Usando datos de prueba como fallback debido a un error general');
        
        // Aquí puedes decidir si quieres mostrar datos de prueba o no
        // Por ahora, no lo haremos para asegurarnos de que el usuario solo avanza con datos válidos
        setServerError({
          type: 'generalError',
          message: 'Ha ocurrido un error al intentar cargar las preguntas. Por favor, inténtalo de nuevo más tarde.'
        });
      }
    } finally {
      setIsSubmitting(false);
      setIsLoadingQuestions(false);
    }
  };

  // Función para reiniciar todo el proceso
  const restart = () => {
    setUserData({
      name: '',
      email: '',
      phone: '',
      dataConsent: false,
      ageRange: ''
    });
    setQuestions([]);
    setAnswers([]);
    setQuizResult(null);
    setCurrentQuestion(0);
    setCurrentStep(0);
    setServerError(null);
  };

  // Función para limpiar el error del servidor
  const clearServerError = () => {
    setServerError(null);
  };

  return (
    <QuizContext.Provider
      value={{
        userData,
        updateUserData,
        questions,
        currentQuestion,
        answers,
        saveAnswer,
        nextQuestion,
        previousQuestion,
        quizResult,
        currentStep,
        setCurrentStep,
        startQuiz,
        restart,
        isSubmitting,
        isLoadingQuestions,
        serverError,
        clearServerError
      }}
    >
      {children}
    </QuizContext.Provider>
  );
};